/**
 * API service for KATA Board v2
 * Handles all API calls for the 10 components
 */

import apiClient from './api';
import { AccessService } from './access';
import type {
  KATABoardV2,
  KATABoardV2Create,
  KATABoardV2Update,
  KATABoardDashboard,
  KATABoardStatistics,
  KATABoardSection,
  KATABoardSectionUpdate,
  Outcome,
  OutcomeCreate,
  Factor,
  FactorCreate,
  PDCACycleV2,
  PDCACycleV2Create,
  PDCACycleV2Update,
  IndicatorDataPoint,
  IndicatorDataPointCreate,
  FileAttachment,
  FileAttachmentCreate,
  BenefitMeasurement,
  BenefitMeasurementCreate,
  KATABoardActivity,
  KATABoardMentee,
  ChartData,
  ProcessObservation,
  ProcessObservationCreate,
  ProcessObservationUpdate,
  PaginatedResponse,
  BenefitSummary,
  KATABoardFilters,
  PDCACycleFilters,
  FileAttachmentFilters,
  Notification,
  NotificationCreate,
  NotificationPreference,
  NotificationPreferenceUpdate,
  NotificationSummary
} from '../types/kataBoardV2';

// Main KATA Board API
export const kataBoardV2Api = {
  // Basic CRUD operations
  getAll: async (filters?: KATABoardFilters): Promise<KATABoardV2[]> => {
    const params = new URLSearchParams();
    if (filters?.workshop) params.append('workshop', filters.workshop.toString());
    if (filters?.mentor) params.append('mentor', filters.mentor.toString());
    if (filters?.created_by) params.append('created_by', filters.created_by.toString());
    
    const response = await apiClient.get(`/kata-boards-v2/?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<KATABoardV2> => {
    const response = await apiClient.get(`/kata-boards-v2/${id}/`);
    return response.data;
  },

  create: async (data: KATABoardV2Create): Promise<KATABoardV2> => {
    const formData = new FormData();
    formData.append('workshop', data.workshop.toString());
    if (data.smart_objective) formData.append('smart_objective', data.smart_objective);
    if (data.mentor) formData.append('mentor', data.mentor.toString());
    if (data.team_description) formData.append('team_description', data.team_description);
    if (data.workshop_image) formData.append('workshop_image', data.workshop_image);

    const response = await apiClient.post('/kata-boards-v2/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  update: async (id: number, data: KATABoardV2Update): Promise<KATABoardV2> => {
    // For SIPOC data and other JSON fields, use JSON content type instead of FormData
    const hasFileUpload = data.workshop_image instanceof File;
    
    if (hasFileUpload) {
      // Use FormData for file uploads
      const formData = new FormData();
      if (data.smart_objective !== undefined) formData.append('smart_objective', data.smart_objective);
      if (data.mentor !== undefined) formData.append('mentor', data.mentor?.toString() || '');
      if (data.team_description !== undefined) formData.append('team_description', data.team_description);
      if (data.workshop_image) formData.append('workshop_image', data.workshop_image);
      if (data.workshop_objective_title !== undefined) formData.append('workshop_objective_title', data.workshop_objective_title);
      if (data.workshop_objective_steps !== undefined) formData.append('workshop_objective_steps', JSON.stringify(data.workshop_objective_steps));
      if (data.sipoc_suppliers !== undefined) formData.append('sipoc_suppliers', JSON.stringify(data.sipoc_suppliers));
      if (data.sipoc_inputs !== undefined) formData.append('sipoc_inputs', JSON.stringify(data.sipoc_inputs));
      if (data.sipoc_processes !== undefined) formData.append('sipoc_processes', JSON.stringify(data.sipoc_processes));
      if (data.sipoc_outputs !== undefined) formData.append('sipoc_outputs', JSON.stringify(data.sipoc_outputs));
      if (data.sipoc_customers !== undefined) formData.append('sipoc_customers', JSON.stringify(data.sipoc_customers));
      if (data.before_status_cost !== undefined) formData.append('before_status_cost', data.before_status_cost === null ? '' : data.before_status_cost.toString());
      if (data.after_status_cost !== undefined) formData.append('after_status_cost', data.after_status_cost === null ? '' : data.after_status_cost.toString());

      const response = await apiClient.patch(`/kata-boards-v2/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // Update endpoint may return a partial serializer; re-fetch canonical board shape.
      if (response.data && response.data.id && response.data.workshop_info) {
        return response.data;
      }
      const refreshed = await apiClient.get(`/kata-boards-v2/${id}/`);
      return refreshed.data;
    } else {
      // Use JSON for non-file updates
      const updateData: any = {};
      if (data.smart_objective !== undefined) updateData.smart_objective = data.smart_objective;
      if (data.mentor !== undefined) updateData.mentor = data.mentor;
      if (data.team_description !== undefined) updateData.team_description = data.team_description;
      if (data.workshop_objective_title !== undefined) updateData.workshop_objective_title = data.workshop_objective_title;
      if (data.workshop_objective_steps !== undefined) updateData.workshop_objective_steps = data.workshop_objective_steps;
      if (data.sipoc_suppliers !== undefined) updateData.sipoc_suppliers = data.sipoc_suppliers;
      if (data.sipoc_inputs !== undefined) updateData.sipoc_inputs = data.sipoc_inputs;
      if (data.sipoc_processes !== undefined) updateData.sipoc_processes = data.sipoc_processes;
      if (data.sipoc_outputs !== undefined) updateData.sipoc_outputs = data.sipoc_outputs;
      if (data.sipoc_customers !== undefined) updateData.sipoc_customers = data.sipoc_customers;
      if (data.before_status_cost !== undefined) updateData.before_status_cost = data.before_status_cost;
      if (data.after_status_cost !== undefined) updateData.after_status_cost = data.after_status_cost;

      const response = await apiClient.patch(`/kata-boards-v2/${id}/`, updateData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      // Update endpoint may return a partial serializer; re-fetch canonical board shape.
      if (response.data && response.data.id && response.data.workshop_info) {
        return response.data;
      }
      const refreshed = await apiClient.get(`/kata-boards-v2/${id}/`);
      return refreshed.data;
    }
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/kata-boards-v2/${id}/`);
  },

  // Dashboard and statistics
  getDashboardList: async (): Promise<KATABoardDashboard[]> => {
    const response = await apiClient.get('/kata-boards-v2/dashboard_list/');
    return response.data;
  },

  getStatistics: async (id: number): Promise<KATABoardStatistics> => {
    const response = await apiClient.get(`/kata-boards-v2/${id}/statistics/`);
    return response.data;
  },

  // Duplication
  duplicate: async (id: number, targetWorkshopId: number): Promise<KATABoardV2> => {
    const response = await apiClient.post(`/kata-boards-v2/${id}/duplicate/`, {
      workshop_id: targetWorkshopId
    });
    return response.data;
  }
};

// Workshop-specific KATA Board API
export const workshopKataBoardApi = {
  getByWorkshop: async (workshopId: number): Promise<KATABoardV2> => {
    const response = await apiClient.get(`/workshop-kata-boards/${workshopId}/`);
    return response.data;
  },

  createOrGet: async (workshopId: number): Promise<KATABoardV2> => {
    const response = await apiClient.post(`/workshop-kata-boards/${workshopId}/create_or_get/`);
    return response.data;
  }
};

// KATA Board Sections API (Components 2, 3, 4, 5)
export const kataBoardSectionApi = {
  getAll: async (kataBoardId?: number, sectionType?: string): Promise<KATABoardSection[]> => {
    const params = new URLSearchParams();
    if (kataBoardId) params.append('kata_board', kataBoardId.toString());
    if (sectionType) params.append('section_type', sectionType);
    
    const response = await apiClient.get(`/kata-board-sections/?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<KATABoardSection> => {
    const response = await apiClient.get(`/kata-board-sections/${id}/`);
    return response.data;
  },

  update: async (id: number, data: KATABoardSectionUpdate): Promise<KATABoardSection> => {
    const response = await apiClient.patch(`/kata-board-sections/${id}/`, data);
    return response.data;
  }
};

// Outcomes API
export const outcomeApi = {
  getAll: async (kataBoardId?: number, outcomeType?: string): Promise<Outcome[]> => {
    const params = new URLSearchParams();
    if (kataBoardId) params.append('kata_board', kataBoardId.toString());
    if (outcomeType) params.append('outcome_type', outcomeType);
    
    const response = await apiClient.get(`/outcomes/?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<Outcome> => {
    const response = await apiClient.get(`/outcomes/${id}/`);
    return response.data;
  },

  create: async (data: OutcomeCreate): Promise<Outcome> => {
    const response = await apiClient.post('/outcomes/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<OutcomeCreate>): Promise<Outcome> => {
    const response = await apiClient.patch(`/outcomes/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/outcomes/${id}/`);
  }
};

// Factors API
export const factorApi = {
  getAll: async (outcomeId?: number): Promise<Factor[]> => {
    const params = new URLSearchParams();
    if (outcomeId) params.append('outcome', outcomeId.toString());
    
    const response = await apiClient.get(`/factors/?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<Factor> => {
    const response = await apiClient.get(`/factors/${id}/`);
    return response.data;
  },

  create: async (data: FactorCreate): Promise<Factor> => {
    const response = await apiClient.post('/factors/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<FactorCreate>): Promise<Factor> => {
    const response = await apiClient.patch(`/factors/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/factors/${id}/`);
  }
};

// PDCA Cycles API (Component 6)
export const pdcaCycleV2Api = {
  getAll: async (
    filters?: PDCACycleFilters,
    page?: number,
    pageSize?: number
  ): Promise<PaginatedResponse<PDCACycleV2>> => {
    const params = new URLSearchParams();
    if (filters?.kata_board) params.append('kata_board', filters.kata_board.toString());
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.responsible_lead) params.append('responsible_lead', filters.responsible_lead.toString());
    if (page !== undefined) params.append('page', page.toString());
    if (pageSize !== undefined) params.append('page_size', pageSize.toString());
    
    const response = await apiClient.get(`/pdca-cycles-v2/?${params.toString()}`);
    
    // Handle paginated response
    if (response.data && typeof response.data === 'object' && 'results' in response.data) {
      return response.data as PaginatedResponse<PDCACycleV2>;
    }
    
    // Handle direct array response (fallback for backward compatibility)
    if (Array.isArray(response.data)) {
      return {
        count: response.data.length,
        next: null,
        previous: null,
        results: response.data
      };
    }
    
    // Empty response fallback
    return {
      count: 0,
      next: null,
      previous: null,
      results: []
    };
  },

  getById: async (id: number): Promise<PDCACycleV2> => {
    const response = await apiClient.get(`/pdca-cycles-v2/${id}/`);
    return response.data;
  },

  create: async (data: PDCACycleV2Create): Promise<PDCACycleV2> => {
    if (data.attachment_files?.length) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'attachment_files' && value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });
      data.attachment_files.forEach(file => formData.append('attachment_files', file));
      const response = await apiClient.post('/pdca-cycles-v2/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
    const response = await apiClient.post('/pdca-cycles-v2/', data);
    return response.data;
  },

  update: async (id: number, data: PDCACycleV2Update): Promise<PDCACycleV2> => {
    const response = await apiClient.patch(`/pdca-cycles-v2/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/pdca-cycles-v2/${id}/`);
  },

  // Special actions
  advanceStatus: async (id: number): Promise<PDCACycleV2> => {
    const response = await apiClient.post(`/pdca-cycles-v2/${id}/advance_status/`);
    return response.data;
  },

  getOverdue: async (): Promise<PDCACycleV2[]> => {
    const response = await apiClient.get('/pdca-cycles-v2/overdue/');
    return response.data;
  },

  sendNotifications: async (): Promise<{ notifications_sent: number }> => {
    const response = await apiClient.post('/pdca-cycles-v2/send_notifications/');
    return response.data;
  }
};

// Indicator Data Points API (Component 7)
export const indicatorDataPointApi = {
  getAll: async (outcomeId?: number, factorId?: number): Promise<IndicatorDataPoint[]> => {
    const params = new URLSearchParams();
    if (outcomeId) params.append('outcome', outcomeId.toString());
    if (factorId) params.append('factor', factorId.toString());
    
    const response = await apiClient.get(`/indicator-data-points/?${params.toString()}`);
    
    // Handle paginated response
    if (response.data && response.data.results) {
      return response.data.results;
    }
    return Array.isArray(response.data) ? response.data : [];
  },

  getById: async (id: number): Promise<IndicatorDataPoint> => {
    const response = await apiClient.get(`/indicator-data-points/${id}/`);
    return response.data;
  },

  create: async (data: IndicatorDataPointCreate): Promise<IndicatorDataPoint> => {
    const response = await apiClient.post('/indicator-data-points/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<IndicatorDataPointCreate>): Promise<IndicatorDataPoint> => {
    const response = await apiClient.patch(`/indicator-data-points/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/indicator-data-points/${id}/`);
  },

  // Chart data
  getChartData: async (outcomeId?: number, workshopId?: number, displayMode?: 'daily' | 'weekly' | 'monthly', factorId?: number): Promise<ChartData> => {
    const params = new URLSearchParams();
    if (factorId) {
      params.append('factor_id', factorId.toString());
      if (displayMode) {
        params.append('display_mode', displayMode);
      }
    } else if (outcomeId) {
      params.append('outcome_id', outcomeId.toString());
    } else if (workshopId) {
      params.append('workshop_id', workshopId.toString());
      if (displayMode) {
        params.append('display_mode', displayMode);
      }
    } else {
      throw new Error('Either outcomeId, workshopId, or factorId must be provided');
    }
    
    const response = await apiClient.get(`/indicator-data-points/chart_data/?${params.toString()}`);
    return response.data;
  }
};

// File Attachments API (Components 1, 4, 5, 8, 9)
// Process Observations API
export const processObservationApi = {
  // Get all observations for a kata board
  getAll: async (kataBoardId: number, observationType?: string): Promise<ProcessObservation[]> => {
    const params = new URLSearchParams({ kata_board: kataBoardId.toString() });
    if (observationType) {
      params.append('type', observationType);
    }
    
    const response = await apiClient.get(`/process-observations/?${params.toString()}`);
    
    // Handle paginated response
    const data = response.data;
    if (data && typeof data === 'object' && 'results' in data) {
      // Paginated response from Django REST Framework
      return data.results;
    } else if (Array.isArray(data)) {
      // Direct array response
      return data;
    } else {
      // Fallback to empty array
      console.warn('Unexpected API response format:', data);
      return [];
    }
  },

  // Get single observation
  getById: async (id: number): Promise<ProcessObservation> => {
    const response = await apiClient.get(`/process-observations/${id}/`);
    return response.data;
  },

  // Create new observation
  create: async (data: ProcessObservationCreate): Promise<ProcessObservation> => {
    const response = await apiClient.post('/process-observations/', data);
    return response.data;
  },

  // Update observation
  update: async (id: number, data: ProcessObservationUpdate): Promise<ProcessObservation> => {
    const response = await apiClient.patch(`/process-observations/${id}/`, data);
    return response.data;
  },

  // Delete observation
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/process-observations/${id}/`);
  }
};

export const fileAttachmentApi = {
  getAll: async (filters?: FileAttachmentFilters): Promise<FileAttachment[]> => {
    const params = new URLSearchParams();
    if (filters?.kata_board) params.append('kata_board', filters.kata_board.toString());
    if (filters?.pdca_cycle) params.append('pdca_cycle', filters.pdca_cycle.toString());
    if (filters?.attachment_type) params.append('attachment_type', filters.attachment_type);
    if (filters?.is_before_image !== undefined) params.append('is_before_image', filters.is_before_image.toString());
    if (filters?.is_after_image !== undefined) params.append('is_after_image', filters.is_after_image.toString());
    
    const response = await apiClient.get(`/file-attachments/?${params.toString()}`);
    
    // Handle paginated response
    if (response.data && typeof response.data === 'object' && 'results' in response.data) {
      return response.data.results;
    }
    
    // Handle direct array response (fallback)
    return Array.isArray(response.data) ? response.data : [];
  },

  getById: async (id: number): Promise<FileAttachment> => {
    const response = await apiClient.get(`/file-attachments/${id}/`);
    return response.data;
  },

  create: async (data: FileAttachmentCreate): Promise<FileAttachment> => {
    const formData = new FormData();
    formData.append('kata_board', data.kata_board.toString());
    formData.append('attachment_type', data.attachment_type);
    if (data.pdca_cycle !== undefined) formData.append('pdca_cycle', data.pdca_cycle.toString());
    formData.append('file', data.file);
    if (data.title) formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    if (data.is_before_image) formData.append('is_before_image', 'true');
    if (data.is_after_image) formData.append('is_after_image', 'true');

    const response = await apiClient.post('/file-attachments/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  update: async (id: number, data: Partial<FileAttachmentCreate>): Promise<FileAttachment> => {
    const formData = new FormData();
    if (data.file) formData.append('file', data.file);
    if (data.title !== undefined) formData.append('title', data.title);
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.is_before_image !== undefined) formData.append('is_before_image', data.is_before_image.toString());
    if (data.is_after_image !== undefined) formData.append('is_after_image', data.is_after_image.toString());

    const response = await apiClient.patch(`/file-attachments/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/file-attachments/${id}/`);
  },

  download: async (id: number): Promise<Blob> => {
    const response = await apiClient.get(`/file-attachments/${id}/serve/`, { responseType: 'blob' });
    return response.data;
  },

  // Get files grouped by type
  getByType: async (kataBoardId: number): Promise<Record<string, FileAttachment[]>> => {
    const response = await apiClient.get(`/file-attachments/by_type/?kata_board_id=${kataBoardId}`);
    return response.data;
  },

  // Get file URL for download
  getDownloadUrl: (fileAttachment: FileAttachment): string => {
    return fileAttachment.file_url || fileAttachment.file;
  },

  // Check if file can be previewed
  canPreview: (fileAttachment: FileAttachment): boolean => {
    return fileAttachment.is_image || fileAttachment.is_pdf;
  },

  // Get file type icon
  getFileIcon: (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.includes('word')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('sheet')) return '📊';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📽️';
    if (mimeType.startsWith('text/')) return '📃';
    return '📁';
  }
};

// Benefit Measurements API (Component 10)
export const benefitMeasurementApi = {
  // Get all benefit measurements with filtering
  getAll: async (filters?: { kata_board?: number; measurement_type?: string; impact_level?: string }): Promise<BenefitMeasurement[]> => {
    const params = new URLSearchParams();
    if (filters?.kata_board) params.append('kata_board', filters.kata_board.toString());
    if (filters?.measurement_type) params.append('measurement_type', filters.measurement_type);
    if (filters?.impact_level) params.append('impact_level', filters.impact_level);
    
    const response = await apiClient.get(`/benefit-measurements/?${params.toString()}`);
    
    // Handle paginated response
    if (response.data && response.data.results) {
      return response.data.results;
    }
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get single benefit measurement
  getById: async (id: number): Promise<BenefitMeasurement> => {
    const response = await apiClient.get(`/benefit-measurements/${id}/`);
    return response.data;
  },

  // Create new benefit measurement
  create: async (data: BenefitMeasurementCreate): Promise<BenefitMeasurement> => {
    const response = await apiClient.post('/benefit-measurements/', data);
    return response.data;
  },

  // Update benefit measurement
  update: async (id: number, data: Partial<BenefitMeasurementCreate>): Promise<BenefitMeasurement> => {
    const response = await apiClient.patch(`/benefit-measurements/${id}/`, data);
    return response.data;
  },

  // Delete benefit measurement
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/benefit-measurements/${id}/`);
  },

  // Get benefit summary
  getSummary: async (kataBoardId: number): Promise<any> => {
    const response = await apiClient.get(`/benefit-measurements/summary/?kata_board_id=${kataBoardId}`);
    return response.data;
  }
};

// Activity Log API
export const kataBoardActivityApi = {
  getAll: async (kataBoardId?: number, action?: string): Promise<KATABoardActivity[]> => {
    const params = new URLSearchParams();
    if (kataBoardId) params.append('kata_board', kataBoardId.toString());
    if (action) params.append('action', action);
    
    const response = await apiClient.get(`/kata-board-activities/?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<KATABoardActivity> => {
    const response = await apiClient.get(`/kata-board-activities/${id}/`);
    return response.data;
  }
};

// Mentee Management API
export const kataBoardMenteeApi = {
  getAll: async (kataBoardId?: number): Promise<KATABoardMentee[]> => {
    const params = new URLSearchParams();
    if (kataBoardId) params.append('kata_board', kataBoardId.toString());
    
    const response = await apiClient.get(`/kata-board-mentees/?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<KATABoardMentee> => {
    const response = await apiClient.get(`/kata-board-mentees/${id}/`);
    return response.data;
  },

  create: async (data: { kata_board: number; mentee: number; role_description?: string }): Promise<KATABoardMentee> => {
    const response = await apiClient.post('/kata-board-mentees/', data);
    return response.data;
  },

  update: async (id: number, data: { role_description?: string }): Promise<KATABoardMentee> => {
    const response = await apiClient.patch(`/kata-board-mentees/${id}/`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/kata-board-mentees/${id}/`);
  }
};

// Utility functions
export const kataBoardUtils = {
  // Format file size
  formatFileSize: (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  },

  // Get priority color class
  getPriorityColor: (priority: string): string => {
    const colors = {
      high: 'text-red-600 bg-red-100',
      medium: 'text-yellow-600 bg-yellow-100',
      low: 'text-green-600 bg-green-100'
    };
    return colors[priority as keyof typeof colors] || colors.medium;
  },

  // Get status color class
  getStatusColor: (status: string): string => {
    const colors = {
      assigned: 'text-blue-600 bg-blue-100',
      in_progress: 'text-yellow-600 bg-yellow-100',
      completed: 'text-green-600 bg-green-100',
      overdue: 'text-red-600 bg-red-100',
      cancelled: 'text-gray-600 bg-gray-100'
    };
    return colors[status as keyof typeof colors] || colors.assigned;
  },

  // Check if file is image
  isImageFile: (mimeType: string): boolean => {
    return mimeType.startsWith('image/');
  },

  // Check if file is PDF
  isPdfFile: (mimeType: string): boolean => {
    return mimeType === 'application/pdf';
  },

  // Format date for display
  formatDate: (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  // Format datetime for display
  formatDateTime: (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  // Calculate progress percentage
  calculateProgress: (completed: number, total: number): number => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  },

  // Get section display name
  getSectionDisplayName: (sectionType: string): string => {
    const names = {
      problem_statement: 'Problem Statement/Current Condition',
      target_condition: 'Target Condition',
      process_map: 'Process Map/Layout',
      process_observations: 'Process Observations'
    };
    return names[sectionType as keyof typeof names] || sectionType;
  },

  // Get attachment type display name
  getAttachmentTypeDisplayName: (attachmentType: string): string => {
    const names = {
      workshop_image: 'Workshop Image',
      process_map: 'Process Map/Layout',
      observation_file: 'Process Observation File',
      before_image: 'Before Status Image',
      after_image: 'After Status Image',
      pdca_file: 'PDCA Cycle File'
    };
    return names[attachmentType as keyof typeof names] || attachmentType;
  },

  // Get benefit type display name
  getBenefitTypeDisplayName: (benefitType: string): string => {
    const names = {
      time_saved: 'Time Saved',
      space_optimized: 'Space Optimized',
      energy_reduction: 'Energy Reduction',
      other_improvement: 'Other Improvement',
      investment_required: 'Investment Required',
      safety_improvement: 'Safety Improvement',
      quality_improvement: 'Quality Improvement',
      delivery_improvement: 'Delivery Improvement',
      cost_reduction: 'Cost Reduction',
      morale_improvement: 'Morale Improvement'
    };
    return names[benefitType as keyof typeof names] || benefitType;
  }
};

// Users API
export const usersApi = {
  getAll: async (): Promise<any[]> => {
    try {
      return await AccessService.getAllDirectoryUsers();
    } catch (error) {
      console.error('Failed to fetch users:', error);
      return [];
    }
  },

  getById: async (id: number): Promise<any> => {
    const users = await AccessService.getAllDirectoryUsers({ search: String(id) });
    return users.find((user) => user.id === id) || null;
  },

  search: async (query: string): Promise<any[]> => {
    try {
      return await AccessService.getAllDirectoryUsers({ search: query });
    } catch (error) {
      console.error('Failed to search users:', error);
      return [];
    }
  }
};

// Notification API
export const notificationApi = {
  // Get user notifications with count
  getAll: async (params?: {
    notification_type?: string;
    is_read?: boolean;
    priority?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ notifications: Notification[], unread_count: number }> => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.notification_type) searchParams.append('notification_type', params.notification_type);
      if (params?.is_read !== undefined) searchParams.append('is_read', params.is_read.toString());
      if (params?.priority) searchParams.append('priority', params.priority);
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.offset) searchParams.append('offset', params.offset.toString());
      
      // Add include_count parameter to get unread count in same request
      searchParams.append('include_count', 'true');

      const response = await apiClient.get(`/notifications/?${searchParams.toString()}`);
      
      // Handle paginated response with count
      if (response.data && typeof response.data === 'object') {
        return {
          notifications: response.data.results || (Array.isArray(response.data) ? response.data : []),
          unread_count: response.data.unread_count || 0
        };
      }
      return {
        notifications: Array.isArray(response.data) ? response.data : [],
        unread_count: 0
      };
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return { notifications: [], unread_count: 0 };
    }
  },

  // Get notification by ID
  getById: async (id: number): Promise<Notification | null> => {
    try {
      const response = await apiClient.get(`/notifications/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch notification:', error);
      return null;
    }
  },

  // Create notification
  create: async (data: NotificationCreate): Promise<Notification | null> => {
    try {
      const response = await apiClient.post('/notifications/', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create notification:', error);
      return null;
    }
  },

  // Mark notification as read
  markAsRead: async (id: number): Promise<boolean> => {
    try {
      await apiClient.post(`/notifications/${id}/mark_read/`);
      return true;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return false;
    }
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<{ count: number }> => {
    try {
      const response = await apiClient.post('/notifications/mark_all_read/');
      return response.data;
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      return { count: 0 };
    }
  },

  // Mark selected notifications as read
  markSelectedAsRead: async (notificationIds: number[]): Promise<{ count: number }> => {
    try {
      const response = await apiClient.post('/notifications/mark_selected_read/', {
        notification_ids: notificationIds
      });
      return response.data;
    } catch (error) {
      console.error('Failed to mark selected notifications as read:', error);
      return { count: 0 };
    }
  },

  // Get unread count
  getUnreadCount: async (): Promise<number> => {
    try {
      const response = await apiClient.get('/notifications/unread_count/');
      return response.data.unread_count || 0;
    } catch (error) {
      console.error('Failed to get unread count:', error);
      return 0;
    }
  },

  // Get notification summary
  getSummary: async (): Promise<NotificationSummary | null> => {
    try {
      const response = await apiClient.get('/notifications/summary/');
      return response.data;
    } catch (error) {
      console.error('Failed to get notification summary:', error);
      return null;
    }
  },

  // Delete notification
  delete: async (id: number): Promise<boolean> => {
    try {
      await apiClient.delete(`/notifications/${id}/`);
      return true;
    } catch (error) {
      console.error('Failed to delete notification:', error);
      return false;
    }
  }
};

// Notification Preferences API
export const notificationPreferenceApi = {
  // Get current user's preferences
  getCurrent: async (): Promise<NotificationPreference | null> => {
    try {
      const response = await apiClient.get('/notification-preferences/current/');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
      return null;
    }
  },

  // Update preferences
  update: async (data: NotificationPreferenceUpdate): Promise<NotificationPreference | null> => {
    try {
      // First get current preferences to get the ID
      const current = await notificationPreferenceApi.getCurrent();
      if (!current) {
        console.error('No current preferences found');
        return null;
      }

      const response = await apiClient.patch(`/notification-preferences/${current.id}/`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      return null;
    }
  }
};
